import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, UserX, Clock, Eye, AlertCircle, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DemoUser {
  id: string;
  user_id: string;
  company_id: string;
  created_at: string;
  expires_at: string;
  status: string;
  profiles?: {
    full_name: string;
    email: string;
  };
}

export default function DemoUsersPage() {
  const [userToRevoke, setUserToRevoke] = useState<DemoUser | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: demoUsers, isLoading } = useQuery({
    queryKey: ['demo-users'],
    queryFn: async () => {
      // First get demo users
      const { data: demoData, error: demoError } = await supabase
        .from('demo_users_expiry')
        .select('*')
        .order('created_at', { ascending: false });

      if (demoError) throw demoError;
      if (!demoData) return [];

      // Then get profiles for each user
      const userIds = demoData.map(d => d.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Merge data
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]));
      return demoData.map(demo => ({
        ...demo,
        profiles: profilesMap.get(demo.user_id)
      })) as DemoUser[];
    }
  });

  const revokeMutation = useMutation({
    mutationFn: async (demoUserId: string) => {
      // Remove from user_companies
      const demoUser = demoUsers?.find(u => u.id === demoUserId);
      if (!demoUser) throw new Error('Demo user not found');

      const { error: removeError } = await supabase
        .from('user_companies')
        .delete()
        .eq('user_id', demoUser.user_id)
        .eq('company_id', demoUser.company_id);

      if (removeError) throw removeError;

      // Update status
      const { error: updateError } = await supabase
        .from('demo_users_expiry')
        .update({ 
          status: 'revoked',
          revoked_at: new Date().toISOString()
        })
        .eq('id', demoUserId);

      if (updateError) throw updateError;

      // Log audit
      await supabase
        .from('audit_logs')
        .insert({
          action: 'demo.revoked',
          entity_type: 'user',
          entity_id: demoUser.user_id,
          company_id: demoUser.company_id,
          meta: {
            revoked_by: 'admin',
            expires_at: demoUser.expires_at
          }
        });
    },
    onSuccess: () => {
      toast({
        title: "Acesso revogado",
        description: "O acesso demo foi revogado com sucesso.",
      });
      queryClient.invalidateQueries({ queryKey: ['demo-users'] });
      setUserToRevoke(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao revogar acesso",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const getDaysRemaining = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const days = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cleanup-demo-users');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Limpeza executada",
        description: `${data.successfully_removed || 0} usuário(s) removido(s)`,
      });
      queryClient.invalidateQueries({ queryKey: ['demo-users'] });
    },
    onError: (error: any) => {
      toast({
        title: "Erro na limpeza",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string, expiresAt: string) => {
    const now = new Date();
    const expiration = new Date(expiresAt);
    const isExpired = now > expiration;
    const daysRemaining = getDaysRemaining(expiresAt);

    if (status === 'revoked') {
      return <Badge variant="destructive">Revogado</Badge>;
    }

    if (status === 'converted') {
      return <Badge className="bg-green-600 hover:bg-green-700">Convertido</Badge>;
    }

    if (status === 'expired' || isExpired) {
      return <Badge variant="secondary">Expirado</Badge>;
    }

    if (status === 'active') {
      if (daysRemaining <= 0) {
        return <Badge variant="secondary">Expirado</Badge>;
      }
      if (daysRemaining <= 2) {
        return <Badge variant="warning" className="bg-yellow-500">Expirando em {daysRemaining}d</Badge>;
      }
      return <Badge variant="default">Ativo ({daysRemaining}d restantes)</Badge>;
    }

    return <Badge variant="outline">{status}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeUsers = demoUsers?.filter(u => u.status === 'active') || [];
  const expiredUsers = demoUsers?.filter(u => u.status === 'expired') || [];
  const revokedUsers = demoUsers?.filter(u => u.status === 'revoked') || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Usuários Demo</h1>
          <p className="text-muted-foreground">
            Gerencie usuários com acesso demo à Empresa de Demonstração
          </p>
        </div>
        <Button
          onClick={() => cleanupMutation.mutate()}
          disabled={cleanupMutation.isPending}
          variant="outline"
        >
          {cleanupMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Limpando...
            </>
          ) : (
            <>
              <Trash2 className="mr-2 h-4 w-4" />
              Executar Limpeza Agora
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Expirados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{expiredUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Revogados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{revokedUsers.length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Usuários Demo
          </CardTitle>
          <CardDescription>
            Lista de todos os usuários com acesso demo ao FleetONE
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!demoUsers || demoUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum usuário demo encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Criado</TableHead>
                  <TableHead>Expira</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {demoUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.profiles?.full_name || '-'}
                    </TableCell>
                    <TableCell>{user.profiles?.email || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(user.created_at), { 
                        addSuffix: true,
                        locale: ptBR 
                      })}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(user.expires_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(user.status, user.expires_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      {user.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUserToRevoke(user)}
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Revogar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!userToRevoke} onOpenChange={() => setUserToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar acesso demo?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja revogar o acesso demo de{' '}
              <strong>{userToRevoke?.profiles?.full_name}</strong>?
              O usuário não poderá mais acessar a Empresa de Demonstração.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToRevoke && revokeMutation.mutate(userToRevoke.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revogando...
                </>
              ) : (
                'Revogar Acesso'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}